import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { errorText } from "./form-errors";

export type VehiclePhoto = { id: string; name: string; path?: string };

export default function VehiclePhotos({
  id = "vehicle-files",
  name = "vehicle[files][]",
  files,
  onChange,
  error,
  help,
  existingPhotos = [],
  retainedName,
  removeName,
  removedIds,
  onRemovedIdsChange,
}: {
  id?: string;
  name?: string;
  files?: File[];
  onChange?: (files: File[]) => void;
  error?: unknown;
  help?: ReactNode;
  existingPhotos?: VehiclePhoto[];
  retainedName?: string;
  removeName?: string;
  removedIds?: string[];
  onRemovedIdsChange?: (ids: string[]) => void;
}) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [removedPhotos, setRemovedPhotos] = useState<string[]>([]);
  const removed = removedIds ?? removedPhotos;
  const selectedFiles = files ?? pendingFiles;
  useEffect(() => {
    if (!input.current) return;
    const selection = new DataTransfer();
    selectedFiles.forEach((file) => selection.items.add(file));
    input.current.files = selection.files;
  }, [selectedFiles]);
  function updateFiles(next: File[]) {
    setPendingFiles(next);
    onChange?.(next);
  }
  function removeSavedPhoto(photoId: string, checked: boolean) {
    const next = checked
      ? [...removed, photoId]
      : removed.filter((id) => id !== photoId);
    setRemovedPhotos(next);
    onRemovedIdsChange?.(next);
  }
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{t("vehicles.photos")}</Label>
      <Input
        ref={input}
        id={id}
        name={name}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={(event) =>
          updateFiles(Array.from(event.currentTarget.files || []))
        }
        aria-describedby={
          errorText(error) ? `${id}-help ${id}-error` : `${id}-help`
        }
        aria-invalid={!!errorText(error)}
      />
      <p id={`${id}-help`} className="text-sm text-muted-foreground">
        {t("vehicles.photo_help")}
        {help ? <> {help}</> : null}
      </p>
      {errorText(error) && (
        <p id={`${id}-error`} className="text-sm text-destructive">
          {errorText(error)}
        </p>
      )}
      {existingPhotos.length > 0 && (
        <ul className="space-y-2">
          {existingPhotos.map((photo) => (
            <li key={photo.id} className="flex items-center gap-2">
              {retainedName && !removed.includes(photo.id) && (
                <input type="hidden" name={retainedName} value={photo.id} />
              )}
              {removeName || retainedName ? (
                <>
                  <Checkbox
                    id={`${id}-remove-${photo.id}`}
                    name={removeName}
                    value={photo.id}
                    checked={removed.includes(photo.id)}
                    onCheckedChange={(checked) =>
                      removeSavedPhoto(photo.id, checked)
                    }
                  />
                  <Label htmlFor={`${id}-remove-${photo.id}`}>
                    {t("vehicles.remove_photo", { name: photo.name })}
                  </Label>
                </>
              ) : (
                <span className="break-words text-sm">{photo.name}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {selectedFiles.length > 0 && (
        <ul className="space-y-2">
          {selectedFiles.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex items-center justify-between gap-3"
            >
              <span className="min-w-0 break-words text-sm">{file.name}</span>
              <Button
                type="button"
                variant="outline"
                aria-label={t("vehicles.remove_photo", { name: file.name })}
                onClick={() =>
                  updateFiles(
                    selectedFiles.filter((_, position) => position !== index),
                  )
                }
              >
                {t("workflow.remove")}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
